import type { ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { OpenItem, OpenLedger } from '@contracts/ledger';
import {
  useLedgerActions,
  useMe,
  useMeetings,
  useNames,
  useOpen,
  useSeriesList,
  useUpcoming,
  useUpdateCommitment,
  useWeekDecisions,
} from '../api/queries';
import {
  Broken,
  Button,
  Card,
  Chip,
  Empty,
  Grade,
  MenuButton,
  OwnerModal,
  AsanaModal,
  Pill,
  Rows,
  Picker,
  SkeletonRows,
  TextArea,
  Who,
  useNotices,
  Icon,
} from '../ui';
import { DueButton } from '../ledger/DueButton';
import {
  ago,
  between,
  clock,
  dayLabel,
  dayLong,
  daysUntil,
  duration,
  groupByDay,
  since,
  until,
  weekdayClock,
} from '../lib/time';
import { answersTo, firstName, guessName } from '../lib/note';
import { useTab, useUrlParam } from '../lib/url-tab';
import { asLeft, useActedHere } from '../ledger/acted-here';

type Side = 'both' | 'mine' | 'theirs';
type Group = 'needs' | 'none' | 'person' | 'series';
const WEEK = 7 * 24 * 60 * 60 * 1000;
/** Untouched for this long and the ledger stops assuming it is still real. */
const MONTH = 30 * 24 * 60 * 60 * 1000;
/** How many rows the top band shows before it offers the rest. */
const BAND = 8;

/** When a commitment was last known to be real: written, or last confirmed. */
const freshness = (i: OpenItem): number =>
  Math.max(Date.parse(i.createdAt), i.keptAt ? Date.parse(i.keptAt) : 0);

/**
 * Board 03 — what you owe, and what is owed to you. Two columns by direction,
 * what nobody owns underneath, what ended this week with its closer named.
 * The title is the day: the ledger is a running account, and today's page of it.
 */
export function Open(): ReactElement {
  const [params, setParams] = useSearchParams();
  const series = params.get('series');
  const person = params.get('person');
  // Which side and how it is grouped, in the address — a reload and Back keep them.
  const [side, setSide] = useTab<Side>('side', ['both', 'mine', 'theirs'], 'both');
  // The narrowing chips too (2026-09-25: "Add filters to the url params as well").
  const [overWeekParam, setOverWeekParam] = useUrlParam('over-week');
  const overWeek = overWeekParam === '1';
  const setOverWeek = (on: boolean) => setOverWeekParam(on ? '1' : null);
  const [groupBy, setGroupBy] = useTab<Group>(
    'group',
    ['needs', 'person', 'series', 'none'],
    'needs',
  );
  const [guessedParam, setGuessedParam] = useUrlParam('guessed');
  const guessed = guessedParam === '1';
  const setGuessed = (on: boolean) => setGuessedParam(on ? '1' : null);
  const open = useOpen({ ...(series ? { series } : {}), ...(person ? { person } : {}) });
  // Taking the scope away keeps the side and the grouping.
  const without = (name: string) => (was: URLSearchParams) => {
    const now = new URLSearchParams(was);
    now.delete(name);
    return now;
  };
  const seriesList = useSeriesList();
  const me = useMe();
  const names = useNames();
  const scopeChip: ReactNode = series ? (
    <Chip
      on
      onClick={() => setParams(without('series'), { replace: true, preventScrollReset: true })}
    >
      {seriesList.data?.find((x) => x.seriesKey === series)?.title ?? series}{' '}
      <Icon name="close" size={12} />
    </Chip>
  ) : person ? (
    <Chip
      on
      onClick={() => setParams(without('person'), { replace: true, preventScrollReset: true })}
    >
      {names.get(person.toLowerCase()) ?? guessName(person)} <Icon name="close" size={12} />
    </Chip>
  ) : null;

  if (open.isPending) {
    return (
      <div className="page stack-lg">
        <h1 className="t-title">{dayLong(new Date().toISOString())}</h1>
        <div className="grid-2">
          <Card flush>
            <SkeletonRows rows={3} />
          </Card>
          <Card flush>
            <SkeletonRows rows={3} />
          </Card>
        </div>
      </div>
    );
  }
  if (open.isError) {
    return (
      <div className="page stack-lg">
        <h1 className="t-title">{dayLong(new Date().toISOString())}</h1>
        <Card flush>
          <Broken what="Your ledger" retry={() => void open.refetch()} />
        </Card>
      </div>
    );
  }
  return (
    <Ledger
      scope={scopeChip}
      groupBy={groupBy}
      setGroupBy={setGroupBy}
      data={open.data}
      side={side}
      setSide={setSide}
      overWeek={overWeek}
      setOverWeek={setOverWeek}
      guessed={guessed}
      setGuessed={setGuessed}
      myEmail={me.data?.email ?? ''}
      names={names}
    />
  );
}

function Ledger(p: {
  data: OpenLedger;
  side: Side;
  setSide: (s: Side) => void;
  overWeek: boolean;
  setOverWeek: (b: boolean) => void;
  guessed: boolean;
  setGuessed: (b: boolean) => void;
  /** A chip naming where the reader arrived from — a series or a person — removable. */
  scope?: ReactNode;
  groupBy: Group;
  setGroupBy: (g: Group) => void;
  myEmail: string;
  names: Map<string, string>;
}): ReactElement {
  const {
    data,
    side,
    setSide,
    overWeek,
    setOverWeek,
    guessed,
    setGuessed,

    myEmail,
    names,
  } = p;
  const now = Date.now();
  // What was closed here stays where it was, crossed out, until the window
  // loses focus (Vladimir, 2026-09-25) — `ledger/acted-here`.
  const acted = useActedHere();
  const left = asLeft(data.items, data.closedThisWeek, acted);
  const all = left.open;
  const mineAll = all.filter((i) => i.direction === 'I_OWE');
  const theirsAll = all.filter((i) => i.direction === 'OWED_TO_ME' && i.ownerEmail);
  const narrow = (i: OpenItem): boolean =>
    (!overWeek || now - Date.parse(i.createdAt) > WEEK) && (!guessed || i.grade === 'ASSUMED');
  const iOwe = mineAll.filter(narrow);
  const owedToMe = theirsAll.filter(narrow);
  const unowned = all.filter((i) => !i.ownerEmail && narrow(i));
  const elsewhere = all.filter((i) => i.direction === 'SOMEONE_ELSE' && i.ownerEmail && narrow(i));
  // Dense above fifty rows, by the rule alone — the toggle went on 2026-09-24
  // ("Remove button dense from the open section, its useless").
  const dense = all.length > 50;
  const groupBy = p.groupBy;
  const nothingAtAll = all.length === 0 && data.closedThisWeek.length === 0;
  // ⚠ Closed recently follows the same owner filters as everything above it
  // (Vladimir, 2026-09-25: "Closed recently also shall be filtered by owner
  // filter at the top of the page") — which side it was on, and whether its
  // owner was a guess.
  const closedShown = left.closed.filter(
    (i) =>
      (side !== 'mine' || i.direction === 'I_OWE') &&
      (side !== 'theirs' || i.direction === 'OWED_TO_ME') &&
      (!guessed || i.grade === 'ASSUMED'),
  );
  // A thin week: two near-empty columns say less than what the week did produce.
  const thin = all.length > 0 && all.length <= 3;

  return (
    <div className="page stack-lg">
      <div className="page-head">
        <div className="stack" style={{ gap: 4 }}>
          <h1 className="t-title">{dayLong(new Date().toISOString())}</h1>
          <p className="mono">
            {data.counts.open} open · {data.counts.needOwner} need an owner · {data.counts.overWeek}{' '}
            over a week
          </p>
        </div>
      </div>

      {nothingAtAll ? (
        <NothingYet />
      ) : (
        <>
          <div className="between">
            <div className="cluster" style={{ gap: 10 }}>
              <span className="t-secondary">Show</span>
              <div className="segmented" role="tablist">
                {(
                  [
                    ['both', 'Both sides', all.length],
                    ['mine', 'You owe', mineAll.length],
                    ['theirs', 'Owed to you', theirsAll.length],
                  ] as Array<[Side, string, number]>
                ).map(([key, label, n]) => (
                  <button
                    key={key}
                    type="button"
                    className={side === key ? 'on' : ''}
                    aria-pressed={side === key}
                    onClick={() => setSide(key)}
                  >
                    {label} <span className="count">{n}</span>
                  </button>
                ))}
              </div>
              <span className="t-secondary" style={{ marginLeft: 6 }}>
                Narrow to
              </span>
              <Chip
                on={overWeek}
                count={all.filter((i) => now - Date.parse(i.createdAt) > WEEK).length}
                onClick={() => setOverWeek(!overWeek)}
              >
                Open over a week
              </Chip>
              <Chip
                on={guessed}
                count={all.filter((i) => i.grade === 'ASSUMED').length}
                onClick={() => setGuessed(!guessed)}
              >
                Owner guessed
              </Chip>
              {p.scope}
            </div>
            <div className="cluster" style={{ gap: 8, flexWrap: 'nowrap' }}>
              <Picker
                aria-label="Group by"
                value={groupBy}
                onChange={p.setGroupBy}
                size="sm"
                width={180}
                align="right"
                options={[
                  { value: 'needs', label: 'Needs you first' },
                  { value: 'none', label: 'Two columns' },
                  { value: 'person', label: 'Group by person' },
                  { value: 'series', label: 'Group by series' },
                ]}
              />
            </div>
          </div>

          {thin ? <ThinWeek open={all.length} /> : null}
          {all.length === 0 ? (
            <NothingOutstanding data={data} />
          ) : groupBy === 'person' || groupBy === 'series' ? (
            <Grouped
              items={[...(side !== 'theirs' ? iOwe : []), ...(side !== 'mine' ? owedToMe : [])]}
              by={groupBy}
              myEmail={myEmail}
              names={names}
            />
          ) : groupBy === 'needs' ? (
            <NeedsYou
              items={[...(side !== 'theirs' ? iOwe : []), ...(side !== 'mine' ? owedToMe : [])]}
              dense={dense}
              myEmail={myEmail}
              names={names}
            />
          ) : (
            <div className="grid-2">
              {side !== 'theirs' ? (
                <Column
                  title="You owe"
                  items={iOwe}
                  dense={dense}
                  myEmail={myEmail}
                  names={names}
                  empty="You owe nobody anything."
                  sub="Nothing in this filter. The next meeting may add something — or close something on the other side."
                />
              ) : null}
              {side !== 'mine' ? (
                <Column
                  title="Owed to you"
                  items={owedToMe}
                  dense={dense}
                  myEmail={myEmail}
                  names={names}
                  empty="Nobody owes you anything."
                  sub="Nothing in this filter — which is either a good week or a quiet one."
                />
              ) : null}
            </div>
          )}

          {unowned.length ? (
            <Card
              title={`Nobody owns these ${unowned.length}`}
              aside={<span className="mono">the app was not sure, so it did not guess</span>}
              flush
            >
              <Rows>
                {unowned.map((i) => (
                  <UnownedRow key={i.id} item={i} myEmail={myEmail} names={names} />
                ))}
              </Rows>
            </Card>
          ) : null}

          {elsewhere.length && side === 'both' ? (
            <Card title={`Between other people, in your spaces · ${elsewhere.length}`} flush>
              <Rows>
                {elsewhere.map((i) => (
                  <ItemRow key={i.id} item={i} dense myEmail={myEmail} names={names} />
                ))}
              </Rows>
            </Card>
          ) : null}

          {closedShown.length ? (
            <ClosedRecently
              items={closedShown}
              capped={data.counts.closed > data.closedThisWeek.length}
              total={data.counts.closed}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * The ledger at the size it actually reaches (board 03, 2026-09-22).
 *
 * Two columns by direction is the right picture of a week and the wrong one of
 * a quarter: it is a flat list, nothing ever leaves it, and every row is the
 * same size as every other. Vladimir, after two days and twenty-seven open
 * items: *"the list of open items begin to be very big, we need a design
 * solution for it not to be just a very long list… its been only two days of
 * using"*. At a dozen a day that is three hundred by the end of the month.
 *
 * So the page opens on a **decision**, not an inventory:
 *
 * - **Needs you now** — what you owe, plus what you are owed and have been
 *   waiting a week for. Full rows, because these are the ones to act on, and
 *   bounded: eight, then the rest on request.
 * - **Everyone else** — one line per person with a count, closed. Twenty rows
 *   become five, and opening one is a click.
 * - **Going stale** — open and unconfirmed for a month. The ledger has no way
 *   for anything to leave except being closed, which is why it only grows;
 *   this is the way out, and *Still on* is not *Done*.
 *
 * Direction is a filter here, not a layout — the switch above still has it.
 */
function NeedsYou({
  items,
  dense,
  myEmail,
  names,
}: {
  items: OpenItem[];
  dense: boolean;
  myEmail: string;
  names: Map<string, string>;
}): ReactElement {
  const [all, setAll] = useState(false);
  const now = Date.now();

  const stale = items.filter((i) => now - freshness(i) > MONTH);
  const live = items.filter((i) => now - freshness(i) <= MONTH);
  // Yours to do, theirs that you have waited long enough for, and anything
  // with a date close enough to matter whoever owns it.
  const needs = live
    .filter(
      (i) =>
        i.direction === 'I_OWE' ||
        now - Date.parse(i.createdAt) > WEEK ||
        (i.dueAt !== null && daysUntil(i.dueAt) <= 3),
    )
    // ⚠ A deadline outranks everything: late first, then soonest, then the
    // undated by when they were promised. This is what the dates are FOR —
    // Vladimir asked for them so the page could say what is actual now.
    .sort(
      (a, b) =>
        (a.dueAt ? daysUntil(a.dueAt) : Infinity) - (b.dueAt ? daysUntil(b.dueAt) : Infinity) ||
        Date.parse(b.createdAt) - Date.parse(a.createdAt),
    );
  const rest = live.filter((i) => !needs.includes(i));

  const shown = all ? needs : needs.slice(0, BAND);

  return (
    <div className="stack-lg">
      <section className="card card-flush">
        <header className="row" style={{ minHeight: 56 }}>
          <h2 className="t-heading cluster" style={{ gap: 10 }}>
            Needs you now
            <span className="count-badge">{needs.length}</span>
          </h2>
          <span className="mono grow" style={{ textAlign: 'right' }}>
            what you owe, and what you have waited a week for
          </span>
        </header>
        {needs.length === 0 ? (
          <Empty tone="ok" lead="Nothing is waiting on you.">
            Everything open is either fresh on somebody else’s side, or below.
          </Empty>
        ) : (
          <>
            <Rows>
              {shown.map((i) => (
                <ItemRow key={i.id} item={i} dense={dense} myEmail={myEmail} names={names} />
              ))}
            </Rows>
            {needs.length > BAND ? (
              <div className="row dense">
                <Button size="sm" variant="quiet" onClick={() => setAll(!all)}>
                  {all ? 'Show the first eight' : `Show all ${needs.length}`}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </section>

      {rest.length ? (
        <Card
          title="Everyone else"
          aside={<span className="mono">{rest.length} open, newer than a week on their side</span>}
          flush
        >
          <PeopleRollup items={rest} dense={dense} myEmail={myEmail} names={names} />
        </Card>
      ) : null}

      {stale.length ? (
        <Card
          title="Going stale"
          aside={<span className="mono">open a month with nobody saying it is still on</span>}
          flush
        >
          <Rows>
            {stale.map((i) => (
              <StaleRow key={i.id} item={i} myEmail={myEmail} names={names} />
            ))}
          </Rows>
        </Card>
      ) : null}
    </div>
  );
}

/**
 * One line per person, closed — and the real rows underneath when it opens.
 *
 * ⚠ The first draft put a cheap two-line link in here instead of the row the
 * rest of the page uses, and Vladimir was right about all of it: *"this rows
 * in the person is too small, on click leads to the transcript and should lead
 * to the commitment page… Rows also shall be easy to close and pass owner and
 * edit the commitment."* Every one of those already worked on `ItemRow`;
 * rolling a person up is a reason to show FEWER rows, never a reason to make
 * the rows you do show worse. So the header is the summary and the contents
 * are the same rows as everywhere else.
 *
 * Several people can be open at once: closing one to read another makes
 * comparing two of them impossible, and comparing is most of why anybody opens
 * two.
 */
function PeopleRollup({
  items,
  dense,
  myEmail,
  names,
}: {
  items: OpenItem[];
  dense: boolean;
  myEmail: string;
  names: Map<string, string>;
}): ReactElement {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const groups = new Map<string, OpenItem[]>();
  for (const i of items) {
    const key = counterparty(i, myEmail) ?? '';
    groups.set(key, [...(groups.get(key) ?? []), i]);
  }
  const ordered = [...groups.entries()].sort((a, b) => b[1].length - a[1].length);
  const toggle = (key: string) =>
    setOpen((was) => {
      const next = new Set(was);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  return (
    <>
      {ordered.map(([email, rows]) => {
        const label = email ? (names.get(email.toLowerCase()) ?? guessName(email)) : 'Nobody named';
        const showing = open.has(email);
        const owe = rows.filter((i) => i.direction === 'I_OWE').length;
        const owed = rows.length - owe;
        const oldest = Math.min(...rows.map((i) => Date.parse(i.createdAt)));
        return (
          <div key={email || '—'} className={`person ${showing ? 'is-open' : ''}`}>
            <button
              type="button"
              className="person-head"
              aria-expanded={showing}
              onClick={() => toggle(email)}
            >
              <span className="chev" aria-hidden="true">
                ▸
              </span>
              <Who email={email} name={label} />
              <span className="grow" />
              <span className="mono person-split">
                {owe ? <span>you owe {owe}</span> : null}
                {owe && owed ? <span className="dot-sep">·</span> : null}
                {owed ? <span>owes you {owed}</span> : null}
              </span>
              <span className="mono person-age">oldest {ago(new Date(oldest).toISOString())}</span>
              <span className="count-badge">{rows.length}</span>
            </button>
            {showing ? (
              <div className="person-rows">
                <Rows>
                  {rows.map((i) => (
                    <ItemRow key={i.id} item={i} dense={dense} myEmail={myEmail} names={names} />
                  ))}
                </Rows>
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

/**
 * A commitment nobody has touched in a month, with the three honest answers.
 *
 * ⚠ **Still on is not Done.** It records that somebody looked and said yes,
 * which is what pushes it out of here for another thirty days — closing it
 * would put a finished stamp on unfinished work, and that is how a ledger
 * stops being worth reading.
 */
function StaleRow({
  item,
  myEmail,
  names,
}: {
  item: OpenItem;
  myEmail: string;
  names: Map<string, string>;
}): ReactElement {
  const ledger = useLedgerActions();
  const { show } = useNotices();
  const who = counterparty(item, myEmail);
  const busy = ledger.keep.isPending || ledger.close.isPending;
  const closed = item.status !== 'OPEN';
  const end = (status: 'DONE' | 'DROPPED') =>
    ledger.close.mutate(
      { id: item.id, body: { status } },
      {
        onSuccess: () =>
          show({
            tone: 'success',
            text: status === 'DONE' ? 'Marked done.' : 'Dropped — it was not a commitment.',
          }),
        onError: (error) => show({ tone: 'failure', text: error.message }),
      },
    );
  return (
    <div className={`row dense-list ${closed ? 'closed-here' : ''}`}>
      <Link to={momentOf(item)} className="row-title grow truncate">
        {item.text}
      </Link>
      <span className="mono" style={{ width: 140, textAlign: 'right' }}>
        {who ? (names.get(who.toLowerCase()) ?? guessName(who)) : '—'}
      </span>
      <span className="mono amber" style={{ width: 64, textAlign: 'right' }}>
        {ago(item.createdAt)}
      </span>
      {closed ? (
        <div className="cluster" style={{ gap: 6 }}>
          <UndoClose item={item} />
        </div>
      ) : (
        <div className="cluster" style={{ gap: 6 }}>
          <Button
            size="sm"
            disabled={busy}
            onClick={() =>
              ledger.keep.mutate(item.id, {
                onSuccess: () =>
                  show({ tone: 'success', text: 'Still on — back in a month if nothing moves.' }),
                onError: (error) => show({ tone: 'failure', text: error.message }),
              })
            }
          >
            Still on
          </Button>
          <Button size="sm" variant="quiet" disabled={busy} onClick={() => end('DONE')}>
            Done
          </Button>
          <Button size="sm" variant="quiet" disabled={busy} onClick={() => end('DROPPED')}>
            Drop it
          </Button>
        </div>
      )}
    </div>
  );
}

function Column({
  title,
  items,
  dense,
  myEmail,
  names,
  empty,
  sub,
}: {
  title: string;
  items: OpenItem[];
  dense: boolean;
  myEmail: string;
  names: Map<string, string>;
  empty: string;
  sub?: string;
}): ReactElement {
  return (
    <section className="card card-flush">
      <header className="row" style={{ minHeight: 56 }}>
        <h2 className="t-heading cluster" style={{ gap: 10 }}>
          {title}
          <span className="count-badge">{items.length}</span>
        </h2>
      </header>
      {items.length === 0 ? (
        <Empty tone="ok" lead={empty}>
          {sub}
        </Empty>
      ) : (
        <Rows>
          {items.map((i) => (
            <ItemRow key={i.id} item={i} dense={dense} myEmail={myEmail} names={names} />
          ))}
        </Rows>
      )}
    </section>
  );
}

/** The meeting, at the moment it was said — the transcript scrolled to the line — or the meeting. */
const momentOf = (i: OpenItem): string =>
  `/m/${i.meeting.id}${i.span.startSegmentId ? `#line-${i.span.startSegmentId}` : ''}`;

/** Who a row is with: the owner when it is owed to you; when you owe it, the other people in the room. */
function counterparty(item: OpenItem, myEmail: string): string | null {
  if (item.direction !== 'I_OWE') return item.ownerEmail;
  const others = item.meeting.attendees.filter(
    (a) =>
      a.toLowerCase() !== myEmail.toLowerCase() &&
      a.toLowerCase() !== item.ownerEmail?.toLowerCase(),
  );
  return others[0] ?? null;
}

function ItemRow({
  item,
  dense,
  myEmail,
  names,
}: {
  item: OpenItem;
  dense: boolean;
  myEmail: string;
  names: Map<string, string>;
}): ReactElement {
  const ledger = useLedgerActions();
  const reword = useUpdateCommitment();
  const { show } = useNotices();
  const [assigning, setAssigning] = useState(false);
  const [toAsana, setToAsana] = useState(false);
  // ⚠ Rewording in the row, not only on the commitment's own page. The
  // extractor gets a name or a noun wrong often enough that going somewhere
  // else to fix six words is the difference between correcting the ledger and
  // learning to distrust it (Vladimir, 2026-09-22: "Rows also shall be easy to
  // close and pass owner and edit the commitment").
  const [draft, setDraft] = useState<string | null>(null);
  const mine = item.direction === 'I_OWE';
  // Closed a moment ago, on this page: still here, crossed out, Undo in reach.
  const closed = item.status !== 'OPEN';
  const ageing = Date.now() - Date.parse(item.createdAt) > WEEK;
  const withEmail = counterparty(item, myEmail);
  const others = item.meeting.attendees.filter(
    (a) => a.toLowerCase() !== myEmail.toLowerCase(),
  ).length;
  const end = (status: 'DONE' | 'DROPPED') =>
    ledger.close.mutate(
      { id: item.id, body: { status } },
      {
        onSuccess: () =>
          show({
            tone: 'success',
            text:
              status === 'DONE'
                ? `Marked done — “${short(item.text)}” has left ${mine ? 'your' : 'their'} side of the ledger.`
                : `Dropped — “${short(item.text)}” leaves every list when you leave this page.`,
            action: { label: 'Undo', onSelect: () => ledger.reopen.mutate(item.id) },
          }),
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  const saveWording = () => {
    const text = (draft ?? '').trim();
    if (text.length < 3 || text === item.text) return setDraft(null);
    reword.mutate(
      { id: item.id, body: { text } },
      {
        onSuccess: () => {
          setDraft(null);
          show({ tone: 'success', text: 'Reworded — and said again in Slack.' });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };
  const candidates = [
    { email: myEmail, name: null, me: true },
    ...item.meeting.attendees
      .filter((a) => a.toLowerCase() !== myEmail.toLowerCase())
      .map((a) => ({ email: a, name: names.get(a.toLowerCase()) ?? guessName(a) })),
  ];
  return (
    <div className={`row stacked ${dense ? 'dense' : ''} ${closed ? 'closed-here' : ''}`}>
      <div className="row-main">
        {draft === null ? (
          <Link to={`/c/${item.id}`} className="row-title" style={{ color: 'inherit' }}>
            {item.text}
          </Link>
        ) : (
          /* ⚠ A textarea, not a one-line field. What a meeting promised is a
             sentence and often two; a field that shows nine words of it and
             scrolls sideways is unusable for the one job it has (Vladimir,
             2026-09-22, within a minute of seeing it). Blur does NOT save —
             surprising in a box you can click out of by accident — so the
             keys are named on screen instead. */
          <div className="stack" style={{ gap: 6, width: '100%' }}>
            <TextArea
              aria-label="What was promised"
              value={draft}
              rows={3}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setDraft(null);
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') saveWording();
              }}
            />
            <div className="cluster" style={{ gap: 8 }}>
              <Button
                size="sm"
                variant="primary"
                onClick={saveWording}
                working={reword.isPending ? 'Saving…' : false}
              >
                Save
              </Button>
              <Button size="sm" variant="quiet" onClick={() => setDraft(null)}>
                Cancel
              </Button>
              <span className="mono">⌘↵ saves · Esc cancels</span>
            </div>
          </div>
        )}
        <span className="row-sub">
          {withEmail ? (
            <Who
              email={withEmail}
              name={names.get(withEmail.toLowerCase()) ?? item.ownerName ?? guessName(withEmail)}
              to={`/people/${encodeURIComponent(withEmail)}`}
            />
          ) : null}
          {mine && others > 1 ? <span className="mono">+{others - 1}</span> : null}
          <Link to={momentOf(item)} className="mono" style={{ color: 'var(--faint)' }}>
            {item.meeting.title} · {weekdayClock(item.meeting.startedAt)}
          </Link>
          <span className={`mono ${ageing ? 'amber' : ''}`}>{ago(item.createdAt)}</span>
          <Grade value={item.grade} />
          <DueButton id={item.id} dueAt={item.dueAt} />
          {item.handedOverFrom ? (
            <Pill>from {firstName(item.handedOverFrom.name, item.handedOverFrom.email)}</Pill>
          ) : null}
          {item.postedToSlack ? <Pill tone="green">in Slack</Pill> : null}
        </span>
      </div>
      <div className="row-actions">
        {closed ? (
          <UndoClose item={item} />
        ) : (
          <>
            <Button
              size="sm"
              onClick={() => end('DONE')}
              working={ledger.close.isPending ? '…' : false}
            >
              {mine ? 'Mark done' : 'Got it'}
            </Button>
            <MenuButton
              size="sm"
              variant="quiet"
              label="···"
              items={[
                {
                  label: 'Reword it…',
                  sub: 'fix what the meeting was heard to promise',
                  onSelect: () => setDraft(item.text),
                },
                {
                  label: mine ? 'Hand it over…' : 'Change the owner…',
                  onSelect: () => setAssigning(true),
                },
                {
                  label: 'Send it to Asana…',
                  sub: 'becomes a task with the meeting’s context, and closes here',
                  onSelect: () => setToAsana(true),
                },
                {
                  label: 'Not a commitment — drop it',
                  onSelect: () => end('DROPPED'),
                  danger: true,
                },
              ]}
            />
          </>
        )}
      </div>
      {assigning ? (
        <OwnerModal
          item={item}
          heard={item.ownerName}
          candidates={candidates}
          onClose={() => setAssigning(false)}
        />
      ) : null}
      {toAsana ? <AsanaModal item={item} onClose={() => setToAsana(false)} /> : null}
    </div>
  );
}

function UnownedRow({
  item,
  myEmail,
  names,
}: {
  item: OpenItem;
  myEmail: string;
  names: Map<string, string>;
}): ReactElement {
  const ledger = useLedgerActions();
  const { show } = useNotices();
  const [assigning, setAssigning] = useState(false);
  // ⚠ Whoever answers to the name the meeting heard goes first. The address
  // was not matched — otherwise this row would not be here — but the name
  // still is, and making somebody hunt for it in a row of three faces is the
  // same complaint as making them type an address.
  const others = item.meeting.attendees
    .filter((a) => a.toLowerCase() !== myEmail.toLowerCase())
    .sort(
      (a, b) =>
        Number(answersTo(item.ownerName, names.get(b.toLowerCase()) ?? null, b)) -
        Number(answersTo(item.ownerName, names.get(a.toLowerCase()) ?? null, a)),
    )
    .slice(0, 3);
  const give = (email: string, name?: string) =>
    ledger.assign.mutate(
      { id: item.id, body: { email, ...(name ? { name } : {}) } },
      {
        onSuccess: (u) =>
          show({ tone: 'success', text: `Owner set — ${u.ownerName ?? u.ownerEmail}.` }),
      },
    );
  if (item.status !== 'OPEN') {
    return (
      <div className="row stacked closed-here">
        <div className="row-main">
          <span className="row-title">“{item.text}”</span>
          <span className="row-sub">
            <Link to={momentOf(item)} className="mono" style={{ color: 'var(--faint)' }}>
              {item.meeting.title} · {weekdayClock(item.meeting.startedAt)}
            </Link>
          </span>
        </div>
        <div className="row-actions">
          <UndoClose item={item} />
        </div>
      </div>
    );
  }
  return (
    <div className="row stacked">
      <div className="row-main">
        <span className="row-title">“{item.text}”</span>
        <span className="row-sub">
          <Link to={momentOf(item)} className="mono" style={{ color: 'var(--faint)' }}>
            {item.meeting.title} · {weekdayClock(item.meeting.startedAt)}
          </Link>
          <Grade value={item.grade} />
        </span>
      </div>
      <div className="row-actions" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <span className="t-secondary">
          {item.ownerName
            ? `The meeting said ${item.ownerName} — which of these?`
            : 'Who owns this?'}
        </span>
        {others.map((a) => (
          <button
            key={a}
            type="button"
            className="who"
            onClick={() => give(a, names.get(a.toLowerCase()) ?? guessName(a))}
          >
            <span className="avatar">
              {(names.get(a.toLowerCase()) ?? guessName(a)).slice(0, 2).toUpperCase()}
            </span>
            {(names.get(a.toLowerCase()) ?? guessName(a)).split(' ')[0]}
          </button>
        ))}
        <Button size="sm" variant="primary" onClick={() => give(myEmail)}>
          Me
        </Button>
        <Button size="sm" onClick={() => setAssigning(true)}>
          Someone else…
        </Button>
        <Button
          size="sm"
          variant="quiet"
          onClick={() =>
            ledger.close.mutate(
              { id: item.id, body: { status: 'DROPPED' } },
              {
                onSuccess: () =>
                  show({
                    tone: 'success',
                    text: 'Not a commitment — dropped.',
                    action: { label: 'Undo', onSelect: () => ledger.reopen.mutate(item.id) },
                  }),
              },
            )
          }
        >
          Not a commitment
        </Button>
      </div>
      {assigning ? (
        <OwnerModal
          item={item}
          heard={item.ownerName}
          candidates={[{ email: myEmail, name: null, me: true }]}
          onClose={() => setAssigning(false)}
        />
      ) : null}
    </div>
  );
}

/** How many ends Closed recently shows before it is asked for more. */
const CLOSED_FIRST = 5;
/** And how many more each time. */
const CLOSED_MORE = 20;

/**
 * The newest ends first, a handful of them, and more on request. Vladimir,
 * 2026-09-25: "Closed recently section becomes very big, need to handle it
 * somehow, it looks weird and might slow the app after scaling". What was
 * closed a moment ago stays in its own row above anyway (`acted-here`); this
 * is for glancing at the week, and a busy week is hundreds.
 */
function ClosedRecently({
  items,
  capped,
  total,
}: {
  items: OpenItem[];
  /** The server sent only the newest of the week's ends. */
  capped: boolean;
  total: number;
}): ReactElement {
  const [shown, setShown] = useState(CLOSED_FIRST);
  const more = Math.min(CLOSED_MORE, items.length - shown);
  return (
    <Card
      title={`Closed recently · ${capped ? `${total} this week` : items.length}`}
      aside={<span className="mono">newest first · every end names who ended it</span>}
      flush
    >
      <Rows>
        {items.slice(0, shown).map((i) => (
          <ClosedRow key={i.id} item={i} />
        ))}
      </Rows>
      {more > 0 || shown > CLOSED_FIRST ? (
        <div className="row dense closed-more" style={{ justifyContent: 'center', gap: 8 }}>
          {more > 0 ? (
            <Button size="sm" variant="quiet" onClick={() => setShown((n) => n + CLOSED_MORE)}>
              Show {more} more
            </Button>
          ) : null}
          {shown > CLOSED_FIRST ? (
            <Button size="sm" variant="quiet" onClick={() => setShown(CLOSED_FIRST)}>
              Show fewer
            </Button>
          ) : null}
          {capped && more <= 0 ? (
            <span className="mono">
              the newest {items.length} — an older end is on its meeting and its own page
            </span>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

/** What a closed commitment became, in a word. */
function closedWord(status: OpenItem['status']): string {
  return status === 'DONE'
    ? 'done'
    : status === 'DROPPED'
      ? 'dropped'
      : status === 'HANDED_OVER'
        ? 'handed over'
        : // ⚠ "in Asana", never "done". Nobody has done it — it moved to the
          // tool the work is tracked in, and a ledger whose "done" can mean
          // "somebody exported it" is a ledger nobody can trust (PRD 51).
          status === 'EXPORTED'
          ? 'in Asana'
          : 'superseded';
}

/**
 * Where Done was, on a row closed a moment ago on this page: what it became,
 * and the way back — one click, in the same place (2026-09-25).
 */
function UndoClose({ item }: { item: OpenItem }): ReactElement {
  const ledger = useLedgerActions();
  const { show } = useNotices();
  return (
    <>
      <Pill tone={item.status === 'DONE' ? 'green' : undefined}>{closedWord(item.status)}</Pill>
      <Button
        size="sm"
        working={ledger.reopen.isPending ? '…' : false}
        onClick={() =>
          ledger.reopen.mutate(item.id, {
            onError: (error) => show({ tone: 'failure', text: error.message }),
          })
        }
      >
        Undo
      </Button>
    </>
  );
}

function ClosedRow({ item }: { item: OpenItem }): ReactElement {
  const ledger = useLedgerActions();
  const word = closedWord(item.status);
  return (
    <div className="row dense closed-row" style={{ opacity: 0.85 }}>
      <div className="row-main">
        <span
          className="row-title"
          style={{
            textDecoration: item.status === 'DONE' ? 'line-through' : 'none',
            color: 'var(--muted)',
          }}
        >
          {item.text}
        </span>
        <span className="row-sub">
          <Pill tone={item.status === 'DONE' ? 'green' : undefined}>{word}</Pill>
          {item.closedBy ? (
            <>
              <span>by</span>
              <Who email={item.closedBy.email} name={item.closedBy.name} />
            </>
          ) : item.closedByMeeting ? (
            <span>
              by what was said in{' '}
              <Link to={`/m/${item.closedByMeeting.id}`}>{item.closedByMeeting.title}</Link>
            </span>
          ) : (
            <span>by a later meeting</span>
          )}
          {item.closedAt ? <span className="mono">{since(item.closedAt)}</span> : null}
          {/* Where it went, one click away — the point of exporting it. */}
          {item.asanaTaskUrl ? (
            <a href={item.asanaTaskUrl} target="_blank" rel="noreferrer" className="mono">
              open the task ↗
            </a>
          ) : null}
          <Link to={`/m/${item.meeting.id}`} className="mono" style={{ color: 'var(--faint)' }}>
            {item.meeting.title}
          </Link>
        </span>
      </div>
      {item.status !== 'SUPERSEDED' ? (
        <div className="row-actions">
          <Button size="sm" variant="quiet" onClick={() => ledger.reopen.mutate(item.id)}>
            Undo
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** 151 commitments at once: one flat list, 34px rows, a header per person or series, nothing hidden. */
function Grouped({
  items,
  by,
  myEmail,
  names,
}: {
  items: OpenItem[];
  by: 'person' | 'series';
  myEmail: string;
  names: Map<string, string>;
}): ReactElement {
  const keyOf = (i: OpenItem): string =>
    by === 'person' ? (counterparty(i, myEmail) ?? '') : (i.seriesKey ?? i.meeting.title);
  const groups = new Map<string, OpenItem[]>();
  for (const i of items) groups.set(keyOf(i), [...(groups.get(keyOf(i)) ?? []), i]);
  const ordered = [...groups.entries()].sort((a, b) => b[1].length - a[1].length);
  if (ordered.length === 0)
    return (
      <Card flush>
        <Empty tone="ok" lead="Nothing outstanding in this view." />
      </Card>
    );
  return (
    <Card flush>
      {ordered.map(([key, rows]) => {
        const owe = rows.filter((i) => i.direction === 'I_OWE').length;
        const owed = rows.length - owe;
        return (
          <div key={key || '—'}>
            <div className="row dense" style={{ background: 'var(--sunk)', minHeight: 36 }}>
              {by === 'person' && key ? (
                <Who
                  email={key}
                  name={names.get(key.toLowerCase()) ?? guessName(key)}
                  to={`/people/${encodeURIComponent(key)}`}
                />
              ) : (
                <span className="row-title">{key || 'nobody in particular'}</span>
              )}
              <span className="mono">
                you owe {owe} · owes you {owed}
              </span>
            </div>
            <Rows>
              {rows.map((i) => {
                const ageing = Date.now() - Date.parse(i.createdAt) > WEEK;
                const withEmail = counterparty(i, myEmail);
                return (
                  <Link
                    key={i.id}
                    to={`/m/${i.meeting.id}`}
                    className={`row dense-list ${i.status !== 'OPEN' ? 'closed-here' : ''}`}
                  >
                    <span className="row-title grow truncate">{i.text}</span>
                    <span className="mono" style={{ width: 160, textAlign: 'right' }}>
                      {by === 'person'
                        ? i.meeting.title
                        : withEmail
                          ? (names.get(withEmail.toLowerCase()) ?? guessName(withEmail))
                          : i.meeting.title}
                    </span>
                    <Grade value={i.grade} />
                    <span
                      className={`mono ${ageing ? 'amber' : ''}`}
                      style={{ width: 64, textAlign: 'right' }}
                    >
                      {ago(i.createdAt)}
                    </span>
                  </Link>
                );
              })}
            </Rows>
          </div>
        );
      })}
    </Card>
  );
}

/** Nothing outstanding gets a whole page rather than a shrug, with three facts on it. */
function NothingOutstanding({ data }: { data: OpenLedger }): ReactElement {
  const upcoming = useUpcoming(7);
  const last = data.closedThisWeek[0];
  const next = (upcoming.data ?? []).find(
    (e) => Date.parse(e.startsAt) > Date.now() && (e.plan === 'WILL_RECORD' || e.capture !== null),
  );
  return (
    <Card>
      <div className="stack-lg">
        <div className="stack" style={{ gap: 6 }}>
          <span className="empty-glyph ok">
            <Icon name="check" size={18} />
          </span>
          <h2 className="t-title" style={{ fontSize: 26 }}>
            Nothing outstanding between you and anyone.
          </h2>
          <p className="t-body" style={{ color: 'var(--muted)', maxWidth: '60ch' }}>
            Both columns are empty and both are meant to be. This is the state the product is for,
            so it gets a whole page rather than a shrug.
          </p>
        </div>
        <dl
          className="stack"
          style={{ gap: 8, borderTop: '1px solid var(--line)', paddingTop: 14 }}
        >
          <div className="cluster" style={{ gap: 16, flexWrap: 'nowrap' }}>
            <dt className="eyebrow" style={{ width: 110, flex: 'none' }}>
              Last closed
            </dt>
            <dd style={{ margin: 0 }}>
              {last ? (
                <>
                  {last.text} —{' '}
                  {last.closedBy
                    ? `${last.status === 'DONE' ? 'done' : last.status.toLowerCase().replace('_', ' ')} by ${last.closedBy.name ?? last.closedBy.email}`
                    : last.closedByMeeting
                      ? `closed by what was said in ${last.closedByMeeting.title}`
                      : 'closed by a later meeting'}
                </>
              ) : (
                'nothing this week'
              )}
            </dd>
          </div>
          <div className="cluster" style={{ gap: 16, flexWrap: 'nowrap' }}>
            <dt className="eyebrow" style={{ width: 110, flex: 'none' }}>
              Next
            </dt>
            <dd style={{ margin: 0 }}>
              {next ? (
                <>
                  {next.title}, {until(next.startsAt)}
                  {next.seriesKey ? (
                    <>
                      {' · '}
                      <Link to={`/series/${encodeURIComponent(next.seriesKey)}`}>
                        what it will bring up
                      </Link>
                    </>
                  ) : null}
                </>
              ) : (
                'nothing on the calendar that will record'
              )}
            </dd>
          </div>
          <div className="cluster" style={{ gap: 16, flexWrap: 'nowrap' }}>
            <dt className="eyebrow" style={{ width: 110, flex: 'none' }}>
              Behind you
            </dt>
            <dd style={{ margin: 0 }}>
              {Math.max(data.counts.closed, data.closedThisWeek.length)} closed this week
            </dd>
          </div>
        </dl>
      </div>
    </Card>
  );
}

/** A thin week: what the week did produce, instead of two near-empty columns. */
function ThinWeek({ open }: { open: number }): ReactElement {
  const meetings = useMeetings();
  const week = (meetings.data ?? []).filter(
    (m) => Date.now() - Date.parse(m.startedAt) < 7 * 86_400_000,
  );
  const decisions = useWeekDecisions(week.map((m) => m.id));
  if (week.length === 0) return <></>;
  const seconds = week.reduce((s, m) => s + between(m.startedAt, m.endedAt), 0);
  const commitments = week.reduce((s, m) => s + m.commitmentCount, 0);
  const standing = decisions.filter((d) => d.status === 'STANDS');
  return (
    <div className="stack-lg">
      <Card>
        <p className="t-body" style={{ maxWidth: '70ch', margin: 0 }}>
          A quiet week. {week.length === 1 ? 'The one meeting' : `The ${week.length} meetings`}{' '}
          recorded
          {commitments === 0
            ? ' produced no promises'
            : ` produced ${commitments} ${commitments === 1 ? 'promise' : 'promises'}`}
          , and only {open} {open === 1 ? 'is' : 'are'} open — so this page shows what the week{' '}
          <em>did</em> produce instead of two near-empty columns.
        </p>
        <p className="mono" style={{ marginTop: 12 }}>
          {week.length} {week.length === 1 ? 'meeting' : 'meetings'} · {duration(seconds)} ·{' '}
          {commitments} {commitments === 1 ? 'commitment' : 'commitments'} · {standing.length}{' '}
          {standing.length === 1 ? 'decision stands' : 'decisions stand'}
        </p>
      </Card>
      {standing.length ? (
        <Card title="What the week decided" flush>
          <Rows>
            {standing.map((d) => (
              <Link key={d.id} to={`/m/${d.meetingId}`} className="row dense">
                <Pill tone="green">stands</Pill>
                <span className="row-title grow">{d.text}</span>
              </Link>
            ))}
          </Rows>
        </Card>
      ) : null}
    </div>
  );
}

function NothingYet(): ReactElement {
  const meetings = useMeetings();
  const upcoming = useUpcoming(7);
  const recorded = meetings.data?.length ?? 0;
  const ahead = upcoming.data ?? [];
  const next = ahead.find((e) => e.plan === 'WILL_RECORD' || e.capture !== null);
  if (recorded > 0) {
    return (
      <Card flush>
        <Empty tone="ok" lead="Nothing outstanding between you and anyone.">
          Both columns are empty and both are meant to be. This is the state the product is for.
        </Empty>
      </Card>
    );
  }
  const days = groupByDay(ahead, (e) => e.startsAt).slice(0, 3);
  return (
    <div className="stack-lg">
      <h2 className="t-heading" style={{ fontSize: 20 }}>
        {next
          ? `Nothing here yet — your first recorded meeting is ${until(next.startsAt)}.`
          : 'Nothing here yet — the columns fill themselves after the first meeting is written up.'}
      </h2>
      {days.length ? (
        <Card title="This week, already marked" flush>
          <Rows>
            {days.map(({ day, items }) => {
              const first = items[0];
              const records = items.filter(
                (e) => e.plan === 'WILL_RECORD' || (e.capture && e.capture !== 'FAILED'),
              );
              return (
                <div key={day} className="row dense">
                  <span className="mono" style={{ width: 110 }}>
                    {dayLabel(day).split(' · ')[0]}
                    {items.length === 1 ? ` ${clock(first.startsAt)}` : ''}
                  </span>
                  <span className="row-title grow">
                    {first.title}
                    {items.length > 1 ? ` · and ${items.length - 1} more` : ''}
                  </span>
                  <span className={`pill ${records.length ? 'pill-green' : 'pill-dashed'}`}>
                    <span
                      className="dot"
                      style={{ background: records.length ? 'var(--green)' : 'var(--hint)' }}
                    />
                    {records.length ? 'Will record' : 'Won’t record'}
                  </span>
                </div>
              );
            })}
          </Rows>
        </Card>
      ) : (
        <Card flush>
          <Empty lead="Nothing on the calendar this week.">
            Meetings with a link appear on <Link to="/meetings">Meetings</Link> the moment they are
            on your calendar, already marked with whether they will be recorded.
          </Empty>
        </Card>
      )}
      <div className="grid-2">
        {['You owe', 'Owed to you'].map((t) => (
          <section key={t} className="card" style={{ background: 'var(--sunk)' }}>
            <h2 className="t-heading" style={{ color: 'var(--muted)', marginBottom: 12 }}>
              {t}
            </h2>
            <div className="stack" style={{ gap: 8 }}>
              <span className="skeleton" style={{ width: '70%' }} />
              <span className="skeleton" style={{ width: '45%' }} />
            </div>
          </section>
        ))}
      </div>
      <p className="t-secondary">An empty ledger with dates on it is a promise.</p>
    </div>
  );
}

const short = (text: string): string => (text.length > 48 ? `${text.slice(0, 46)}…` : text);
